"use client"

import { useState } from "react"
import { Edit, Calendar, MapPin, Share2, CalendarPlus, Ticket } from "lucide-react"
import config from "../config"
import { formatEventDateTime, formatMoney, downloadEventICS } from "../utils/format"

const EventHeader = ({ event, isCreator, isLoggedIn, navigate, event_slug }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const when = formatEventDateTime(event.date, event.time, event.timezone)
  const currency = event.currency || "KES"

  // Determine a price label from tiers (if any) or the flat ticket price.
  const tiers = (event.ticket_tiers || []).filter((t) => t.is_active !== false)
  let priceLabel = "Free"
  if (tiers.length > 0) {
    const prices = tiers.map((t) => Number(t.price || 0))
    const min = Math.min(...prices)
    priceLabel = min > 0 ? `From ${formatMoney(min, currency)}` : "Free"
  } else if (event.is_paid_event && Number(event.ticket_price) > 0) {
    priceLabel = formatMoney(event.ticket_price, currency)
  }

  const handleShare = async () => {
    const url = window.location.href
    const shareData = { title: event.title, text: event.description?.slice(0, 140), url }
    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (e) { /* user dismissed */ }
  }

  return (
    <>
      {/* Hero Image Section */}
      <div className="relative overflow-hidden rounded-2xl shadow-2xl mb-8">
        <div className="relative cursor-pointer group" onClick={() => setIsImageModalOpen(true)}>
          <img
            src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
            alt={event.title}
            className="w-full h-96 object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Category + price badges */}
          <div className="absolute top-6 right-6 flex flex-col items-end gap-2">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg font-medium capitalize">
              {event.category_display || event.category}
            </div>
            <div className="bg-white/95 text-gray-900 px-4 py-2 rounded-full shadow-lg font-semibold flex items-center gap-1">
              <Ticket size={15} /> {priceLabel}
            </div>
          </div>

          {/* Edit Button for Creator */}
          {isLoggedIn && isCreator && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/events/${event_slug}/update`) }}
              className="absolute top-6 left-6 bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-full shadow-lg font-medium transition-all duration-200 flex items-center gap-2"
            >
              <Edit size={16} /> Edit Event
            </button>
          )}

          {/* Event Title Overlay */}
          <div className="absolute bottom-6 left-6 right-6">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg">{event.title}</h1>
            <div className="flex flex-wrap gap-4 text-white/90">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span className="font-medium">{when.full}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} />
                <span className="font-medium">{event.location}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share / Calendar actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <Share2 size={16} /> {copied ? "Link copied!" : "Share"}
        </button>
        <button
          onClick={() => downloadEventICS(event)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <CalendarPlus size={16} /> Add to Calendar
        </button>
        {event.location_url && (
          <a
            href={event.location_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
          >
            <MapPin size={16} /> View Map
          </a>
        )}
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setIsImageModalOpen(false)}>
          <div className="relative max-w-6xl max-h-full">
            <img
              src={`${config.MEDIA_BASE_URL}${event.thumbnail_photo}`}
              alt={event.title}
              className="max-w-full max-h-full rounded-lg shadow-2xl"
            />
            <button
              className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full text-xl transition-colors duration-200"
              onClick={() => setIsImageModalOpen(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default EventHeader
